// i18n/index.ts — точка входа i18n для «Упряжка».
// API:
//   - t(key, vars?)           — вернуть переведённую строку с подстановкой {var}.
//   - useTranslate()          — React-хук с тем же API (для будущего локалесвичa).
//   - setLocale(locale)       — переключить локаль (ru ↔ en) в runtime.
//
// Default locale: ru (бизнес-требование: основной язык РФ-портала).

import { useMemo } from 'react';
import { ru, type Dict } from './ru';
import { en } from './en';

/** Поддерживаемые локали. */
export type Locale = 'ru' | 'en';

/** Словарь текущей локали. */
const dictionaries: Record<Locale, Dict> = { ru, en };

/** Текущая локаль (singleton, mutable для setLocale). */
let currentLocale: Locale = 'ru';

/** Получить текущую локаль. */
export function getLocale(): Locale {
  return currentLocale;
}

/** Переключить локаль. */
export function setLocale(locale: Locale): void {
  currentLocale = locale;
}

/**
 * Перевести ключ с подстановкой переменных.
 *
 * @example
 *   t('auth.login.title')                       // 'Вход в Упряжку'
 *   t('common.greeting', { name: 'Иван' })      // 'Привет, Иван!'
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const dict = dictionaries[currentLocale];
  const value = dict[key] ?? dictionaries.ru[key] ?? key;
  if (!vars) return value;
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)),
    value,
  );
}

/**
 * React-хук: возвращает функцию t, привязанную к текущей локали.
 * В V1-core не подписывается на изменения локали (singleton), но
 * обёртка оставлена для будущего расширения через Context.
 */
export function useTranslate(): (key: string, vars?: Record<string, string | number>) => string {
  return useMemo(() => t, []);
}
